import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import AppleSignin from "react-apple-signin-auth";
import { api, setUser, setToken } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * AppleSignInButton — unified Sign in with Apple control for web (PWA) and
 * native iOS (Capacitor). The native path uses
 * @capacitor-community/apple-sign-in which surfaces the real ASAuthorization
 * sheet; the web path uses Apple JS via react-apple-signin-auth and a popup.
 *
 * Both paths POST the resulting identity_token to /api/auth/apple where the
 * server verifies signature, audience, issuer and either links or creates a
 * customer account, returning the same {user, token} envelope as /auth/login.
 */
// Apple issues identity_tokens whose `aud` claim is the client_id passed at
// authorize-time. Web (PWA) uses the Services ID; native iOS must use the
// app's Bundle ID or ASAuthorizationController returns error 1000. The
// backend accepts both audiences (APPLE_WEB_CLIENT_ID + APPLE_IOS_BUNDLE_ID).
const WEB_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const NATIVE_CLIENT_ID =
  process.env.REACT_APP_APPLE_IOS_BUNDLE_ID || "uk.co.binshare.marketplace";
const REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;

export default function AppleSignInButton({ onAuthenticated }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Show a disabled placeholder if Apple isn't configured — keeps the button
  // visible (App Store guideline 4.8 requires equal placement) without
  // failing silently in dev environments. On native we always have the
  // bundle id, so the button is always live.
  const nativePlatform = Capacitor.isNativePlatform();
  const configured = nativePlatform
    ? Boolean(NATIVE_CLIENT_ID)
    : Boolean(WEB_CLIENT_ID && REDIRECT_URI);

  const handleToken = async (identityToken, profile = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/apple", {
        identity_token: identityToken,
        given_name: profile.givenName || null,
        family_name: profile.familyName || null,
      });
      setToken(data.token);
      setUser(data.user);
      toast.success(`Welcome${data.user?.name ? `, ${data.user.name}` : ""}!`);
      if (onAuthenticated) onAuthenticated(data.user);
      else if (data.user?.needs_role_selection) navigate("/onboarding/role");
      else navigate(data.user?.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in with Apple failed");
    } finally {
      setLoading(false);
    }
  };

  const signInNative = async () => {
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const result = await SignInWithApple.authorize({
        // Native iOS: clientId MUST be the app's Bundle ID, not the web
        // Services ID — otherwise ASAuthorizationController rejects with
        // ASAuthorizationErrorUnknown (=1000).
        clientId: NATIVE_CLIENT_ID,
        redirectURI: REDIRECT_URI || `https://app.binshare.co.uk/login`,
        scopes: "email name",
        state: "binshare-native",
      });
      const token = result?.response?.identityToken;
      if (!token) {
        toast.error("Apple did not return an identity token");
        return;
      }
      await handleToken(token, {
        givenName: result.response?.givenName,
        familyName: result.response?.familyName,
      });
    } catch (err) {
      if (err?.code !== "1001") {
        // 1001 = user cancelled, stay silent
        toast.error(err?.message || "Apple sign-in cancelled");
      }
    }
  };

  // ---- Capacitor native (iOS) -------------------------------------------------
  if (nativePlatform) {
    return (
      <Button
        data-testid="apple-signin-btn-native"
        type="button"
        onClick={signInNative}
        disabled={loading || !configured}
        className="w-full rounded-full bg-black hover:bg-neutral-800 text-white min-h-[44px] flex items-center justify-center gap-2"
      >
        <AppleGlyph />
        <span>{loading ? "Signing in…" : "Sign in with Apple"}</span>
      </Button>
    );
  }

  // ---- Web (PWA) --------------------------------------------------------------
  if (!configured) {
    return (
      <Button
        data-testid="apple-signin-btn-disabled"
        type="button"
        disabled
        className="w-full rounded-full bg-black/60 text-white min-h-[44px] flex items-center justify-center gap-2 cursor-not-allowed"
      >
        <AppleGlyph />
        <span>Sign in with Apple (unavailable)</span>
      </Button>
    );
  }

  return (
    <AppleSignin
      authOptions={{
        clientId: WEB_CLIENT_ID,
        scope: "email name",
        redirectURI: REDIRECT_URI,
        usePopup: true,
      }}
      uiType="dark"
      onSuccess={(resp) => {
        const token = resp?.authorization?.id_token;
        if (!token) {
          toast.error("Apple did not return an identity token");
          return;
        }
        // `user` (with name) is only present on first authorisation.
        const u = resp?.user || {};
        handleToken(token, {
          givenName: u?.name?.firstName,
          familyName: u?.name?.lastName,
        });
      }}
      onError={(err) => {
        if (err && err.error !== "popup_closed_by_user") {
          toast.error("Apple sign-in failed — please try again");
        }
      }}
      render={({ onClick }) => (
        <Button
          data-testid="apple-signin-btn"
          type="button"
          onClick={onClick}
          disabled={loading}
          className="w-full rounded-full bg-black hover:bg-neutral-800 text-white min-h-[44px] flex items-center justify-center gap-2"
        >
          <AppleGlyph />
          <span>{loading ? "Signing in…" : "Sign in with Apple"}</span>
        </Button>
      )}
    />
  );
}

function AppleGlyph() {
  // Inline SVG of Apple's logo glyph — used per Apple HIG (white on black).
  return (
    <svg viewBox="0 0 384 512" width="14" height="16" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}
